import React, { useState, useEffect } from 'react';
import whatsappService from '../services/whatsappService';

interface WhatsAppChatProps {
  phoneNumber?: string;
  autoMessage?: string;
  autoReply?: string;
}

const WhatsAppChat: React.FC<WhatsAppChatProps> = ({ 
  phoneNumber: _phoneNumber, 
  autoMessage = "Hi! I'm interested in your web development services. Can you help me get started?",
  autoReply: _autoReply
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Show the button after 3 seconds
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);



    // Update current time every minute
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    // Show tooltip after 5 seconds and keep it visible
    const tooltipTimer = setTimeout(() => {
      setShowTooltip(true);
    }, 5000);

    return () => {
      clearTimeout(timer);
      clearTimeout(tooltipTimer);
      clearInterval(timeInterval);
    };
  }, []);

  const getWelcomingMessage = () => {
    const hour = currentTime.getHours();
    
    // Dynamic greeting based on user's local time
    let greeting = '';
    if (hour < 6) {
      greeting = '🌙 Good night!';
    } else if (hour < 12) {
      greeting = '🌅 Good morning!';
    } else if (hour < 17) {
      greeting = '☀️ Good afternoon!';
    } else {
      greeting = '🌆 Good evening!';
    }
    
    // Always show welcoming message regardless of business hours
    return `${greeting} Welcome to our website. How can we help you today?`;
  };

  const handleWhatsAppClick = () => {
    // Use the WhatsApp service to handle the chat
    whatsappService.initChat(autoMessage);
    // Don't hide tooltip on click - keep it visible
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50">
      {/* WhatsApp Button */}
      <div
        className={`
          relative bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg 
          cursor-pointer transition-all duration-300 transform hover:scale-110
          ${isHovered ? 'animate-pulse' : ''}
        `}
        onClick={handleWhatsAppClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          animation: isHovered ? 'pulse 1s infinite' : 'none'
        }}
      >
        {/* WhatsApp Icon */}
        <svg 
          className="w-6 h-6" 
          fill="currentColor" 
          viewBox="0 0 24 24"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </svg>
        
        {/* Pulse Animation Ring */}
        <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></div>
      </div>

      {/* Vertically Centered Tooltip with Left Pointer */}
      {showTooltip && (
        <div 
          className="absolute left-16 top-1/2 transform -translate-y-1/2 w-80"
          style={{
            animation: 'tooltipSlideRight 2s ease-in-out infinite'
          }}
        >
          <div className="relative">
            {/* Tooltip Content - Full Width One Liner */}
            <div 
              className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow-xl px-4 py-3 flex items-center justify-between"
              style={{
                animation: 'slideIn 0.6s ease-out, tooltipGlow 2s ease-in-out infinite'
              }}
            >
              <div className="flex items-center space-x-3 flex-1">
                <div 
                  className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    animation: 'iconPulse 1.5s ease-in-out infinite'
                  }}
                >
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                </div>
                <span 
                  className="text-sm font-medium"
                  style={{
                    animation: 'textGlow 2s ease-in-out infinite'
                  }}
                >
                  {getWelcomingMessage()}
                </span>
              </div>
            </div>
            
            {/* Nice Carrot Pointer pointing to WhatsApp icon */}
            <div 
              className="absolute left-0 top-1/2 transform -translate-x-full -translate-y-1/2"
              style={{
                animation: 'pointLeft 2s ease-in-out infinite'
              }}
            >
              {/* Main Carrot */}
              <div className="w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-green-500 relative">
                {/* Inner Carrot for depth */}
                <div className="absolute top-0 left-0 w-0 h-0 border-t-6 border-b-6 border-r-6 border-transparent border-r-green-400 transform translate-x-1 -translate-y-1"></div>
              </div>
              
              {/* Carrot Shadow */}
              <div className="absolute top-0 left-0 w-0 h-0 border-t-8 border-b-8 border-r-8 border-transparent border-r-black opacity-20 transform translate-x-1"></div>
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(-20px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateX(0) scale(1);
            }
          }
          
          @keyframes tooltipSlideRight {
            0%, 100% {
              transform: translateY(-50%) translateX(0);
            }
            50% {
              transform: translateY(-50%) translateX(-10px);
            }
          }
          
          @keyframes tooltipGlow {
            0%, 100% {
              box-shadow: 0 10px 25px rgba(34, 197, 94, 0.3);
            }
            50% {
              box-shadow: 0 15px 35px rgba(34, 197, 94, 0.5);
            }
          }
          
          @keyframes iconPulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.1);
            }
          }
          
          @keyframes textGlow {
            0%, 100% {
              text-shadow: 0 0 5px rgba(255, 255, 255, 0.3);
            }
            50% {
              text-shadow: 0 0 10px rgba(255, 255, 255, 0.6);
            }
          }
          
          @keyframes pointLeft {
            0% {
              transform: translateX(-100%) translateY(-50%);
            }
            50% {
              transform: translateX(-140%) translateY(-50%);
            }
            100% {
              transform: translateX(-100%) translateY(-50%);
            }
          }
        `
      }} />
    </div>
  );
};

export default WhatsAppChat;
